import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Icon, 
  useTheme, 
  useMediaQuery, 
  Divider,
  CircularProgress,
  Fade,
  alpha,
  Grow,
  Button,
  Chip,
  Avatar,
  Stack,
  Skeleton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'; 
import PartnerIcon from '@mui/icons-material/Handshake'; 
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlagIcon from '@mui/icons-material/Flag';
import DownloadIcon from '@mui/icons-material/Download';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';

// Supprimez cette ligne en double :
// import React from 'react';

const getImagePath = (fileName) => {
    if (!fileName || fileName.trim() === '') return null;
    
    const cleanName = fileName.trim();
    const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.JPG', '.JPEG', '.PNG'];
    const hasExtension = extensions.some(ext => cleanName.endsWith(ext));
    
    if (hasExtension) {
        return `/assets/msb/${cleanName}`;
    } else {
        return `/assets/msb/${cleanName}.jpg`;
    }
};

const handleNavigation = () => {
  window.history.back(); 
};

const handleShare = async () => {
  const shareData = {
    title: 'Clôture du 14ème Mois de la Statistique au Burundi - Journée Africaine de la Statistique, Édition 2025',
    text: 'Découvrez les moments forts de la cérémonie de clôture du 14ème Mois de la Statistique au Burundi, célébrant la Journée Africaine de la Statistique 2025.',
    url: window.location.href
  };
  try {
    await navigator.share(shareData);
  } catch (err) {
    console.error('Erreur de partage:', err);
  }
};

// --- Constantes de Couleurs ---
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const tertiaryColor = '#f8f9fa';
const white = '#ffffff';
const textColor = '#2c3e50';
const accentColor = '#1976d2'; // Ajouté pour corriger l'erreur manquante

// --- Composant ImageOptimized avec objectFit adaptable ---
const ImageOptimized = ({ 
  fileId, 
  alt, 
  mb = 4, 
  height = 300, 
  width = '100%',
  priority = false,
  onLoadComplete,
  objectFit = 'contain', // CHANGÉ: 'contain' au lieu de 'cover'
  borderRadius = 1,
  shadow = 1,
  bgColor = '#f5f5f5'
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(true);
    const [imageSrc, setImageSrc] = useState(null);
    
    const imgRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        if (!fileId) {
            setHasError(true);
            return;
        }

        const src = getImagePath(fileId);
        setImageSrc(src);

        if (priority && src) {
            // Chargement immédiat pour les images prioritaires
            const img = new Image();
            img.src = src;
            img.onload = () => {
                setIsLoading(false);
                setImageLoaded(true);
                setShowSkeleton(false);
                if (onLoadComplete) onLoadComplete();
            };
            img.onerror = () => {
                setIsLoading(false);
                setHasError(true);
                setShowSkeleton(false);
            };
        } else if (src) {
            // Lazy loading pour les autres images
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && imgRef.current) {
                            const img = new Image();
                            img.src = src;
                            img.onload = () => {
                                setIsLoading(false);
                                setImageLoaded(true);
                                setShowSkeleton(false);
                                if (onLoadComplete) onLoadComplete();
                            };
                            img.onerror = () => {
                                setIsLoading(false);
                                setHasError(true);
                                setShowSkeleton(false);
                            };
                            observerRef.current.disconnect();
                        }
                    });
                },
                { rootMargin: '100px' }
            );

            if (imgRef.current) {
                observerRef.current.observe(imgRef.current);
            }
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [fileId, priority, onLoadComplete]);

    const handleRetry = () => {
        setHasError(false);
        setIsLoading(true);
        setShowSkeleton(true);
        
        const src = getImagePath(fileId);
        const img = new Image();
        img.src = src;
        img.onload = () => {
            setIsLoading(false);
            setImageLoaded(true);
            setShowSkeleton(false);
        };
        img.onerror = () => {
            setIsLoading(false);
            setHasError(true);
            setShowSkeleton(false);
        };
    };

    if (hasError) {
        return (
            <Box 
                sx={{ 
                    height: height, 
                    mb: mb,
                    width: width,
                    bgcolor: bgColor, 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    border: '1px dashed #ddd',
                    borderRadius: borderRadius,
                    overflow: 'hidden'
                }}
                title={alt}
            >
                <PhotoCameraIcon sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', px: 2 }}>
                    Image non disponible
                </Typography>
                <Button 
                    variant="outlined" 
                    size="small" 
                    sx={{ mt: 2 }}
                    onClick={handleRetry}
                >
                    Réessayer
                </Button>
            </Box>
        );
    }

    return (
        <Box 
            ref={imgRef}
            sx={{ 
                position: 'relative',
                height: height, 
                mb: mb,
                width: width,
                borderRadius: borderRadius,
                overflow: 'hidden',
                backgroundColor: bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {showSkeleton && (
                <Skeleton 
                    variant="rectangular" 
                    width="100%" 
                    height="100%"
                    animation="wave"
                    sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1
                    }}
                />
            )}
            
            {imageSrc && (
                <Box
                    component="img"
                    src={imageSrc}
                    alt={alt}
                    onLoad={() => {
                        setIsLoading(false);
                        setImageLoaded(true);
                        setShowSkeleton(false);
                        if (onLoadComplete) onLoadComplete();
                    }}
                    onError={() => {
                        setIsLoading(false);
                        setHasError(true);
                        setShowSkeleton(false);
                    }}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: objectFit === 'contain' ? 'auto' : '100%',
                        height: objectFit === 'contain' ? 'auto' : '100%',
                        objectFit: objectFit,
                        opacity: imageLoaded ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                        position: 'relative',
                        zIndex: 2
                    }}
                    loading={priority ? "eager" : "lazy"}
                    decoding="async"
                />
            )}
        </Box>
    );
};

// Contenu des discours
const discoursContent = [
    {
        speaker: "Gouverneur de la province de Bujumbura",
        title: "Discours de Bienvenue",
        key_messages: ["Appel à soutenir les efforts de modernisation statistique du Gouvernement.", "Le thème, édition 2025 interpelle toutes les parties prenantes (utilisateurs et bénéficiaires)."],
        icon: CorporateFareIcon,
        image_file: "Cloture_JAS_19-11-2025_Royal_palace-gouverneur.jpg",
        image_alt: "Gouverneur de Bujumbura",
        priority: true,
        objectFit: 'contain' 
    },
    {
        speaker: "M. Judicael Elidje (Représentant Résident de l'UNFPA)",
        title: "Allocution des Partenaires",
        key_messages: ["Encouragement à l'usage de l'Intelligence Artificielle (IA) dans les activités de l'INSBU.", "Les données du RGPHAE sont une base essentielle pour orienter les politiques publiques.", "Réaffirmation de l'appui continu des partenaires pour l'innovation statistique."],
        icon: PartnerIcon,
        image_file: "Cloture_JAS_19-11-2025_Royal_palace-representUNFPA.jpg", 
        image_alt: "M. Judicael Elidje de l'UNFPA",
        priority: false,
        objectFit: 'contain'
    },
    {
        speaker: "M. Nicolas NDAYISHIMIYE (Directeur Général de l'INSBU)",
        title: "Allocution d'Accueil",
        key_messages: ["Remerciement au Premier Ministre, témoignant de l'intérêt du Gouvernement pour le Système Statistique National.", "Le 14ème Mois a été un mois de plaidoyer et de sensibilisation, marqué par le renforcement des capacités et la disponibilisation des produits."],
        icon: RecordVoiceOverIcon,
        image_file: "Cloture_JAS_19-11-2025_Royal_palace-dg.jpg",
        image_alt: "M. Nicolas Ndayishimiye, DG de l'INSBU",
        priority: false,
        objectFit: 'contain'
    },
    {
        speaker: "S.E. Nestor NTAHONTUYE (Premier Ministre)",
        title: "Discours de Circonstance",
        key_messages: ["Le Burundi est fier de s'associer à la communauté africaine pour la célébration de la Journée Africaine de la Statistique.", "Le mois a été un véritable mois de plaidoyer en faveur des innovations et des statistiques de qualité.", "Rappel : la collecte des données du RGPHAE se poursuit jusqu'en 2027."],
        icon: FlagIcon, 
        image_file: "Cloture_JAS_19-11-2025_Royal_palace-ministre.jpg",
        image_alt: "S.E. Nestor NTAHONTUYE, Premier Ministre",
        priority: false,
        objectFit: 'contain'
    },
];

const PresentationCard = ({ title, description, IconComponent, index }) => (
    <Grow in={true} timeout={300 + (index * 100)}>
        <Card 
            sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.3s ease',
                '&:hover': { 
                    transform: 'translateY(-5px)', 
                    boxShadow: 3
                },
                borderLeft: `4px solid ${secondaryColor}`
            }}
        >
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box display="flex" alignItems="center" mb={1.5}>
                    <IconComponent sx={{ color: primaryColor, mr: 1.5, fontSize: 28 }} />
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                        {title}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                    {description}
                </Typography>
            </CardContent>
        </Card>
    </Grow>
);

const engagementsINSBU = [
    { text: "Plaidoyer et sensibilisation à la culture statistique.", icon: RecordVoiceOverIcon },
    { text: "Renforcement des capacités du Système Statistique National.", icon: GroupWorkIcon },
    { text: "Disponibilisation des produits statistiques de qualité.", icon: CheckCircleIcon },
    { text: "Modernisation de la production statistique (Innovations).", icon: EmojiEventsIcon },
    { text: "Promotion de la collaboration pour la collecte des données (RGPHAE).", icon: PartnerIcon },
];

const presentationsCloture = [
    { title: "Synergie Institutionnelle et Partenariats Stratégiques", description: "Pour une statistique innovante au service de la vision Burundi 2040-2060.", icon: GroupWorkIcon },
    { title: "Centralité de l'Évaluation de l' Impact des interventions pour la vision 2040-2060", description: "Mesurer l'atteinte de la vision Pays Émergent (2040) et Pays Développé (2060).", icon: CorporateFareIcon },
    { title: "Rapport de l'Open Data Inventory (ODIN)", description: "Évaluation de la transparence et de l'accessibilité des données nationales.", icon: CheckCircleIcon },
    { title: "Rôle du Système de la Comptabilité Économique et Environnementale (SCEE)", description: "Élaboration des comptes de l'environnement dans le cadre de la vision 2040-2060.", icon: CorporateFareIcon },
    { title: "Résultats du Recensement Général de l'Agriculture et de l'Élevage (RGAE)", description: "Rapport sur les modules de base agricole, exploitations modernes et organisations professionnelles.", icon: EmojiEventsIcon },
];

// --- Composant Principal ---
function ClotureMoisStatistique2025() {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [imagesLoaded, setImagesLoaded] = useState(0);

    const handleImageLoad = () => {
        setImagesLoaded(prev => prev + 1);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: tertiaryColor
        }}>
            <NavigationBar />
            
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header avec navigation */}
                <Fade in={true} timeout={500}>
                    <Box sx={{ mb: 4 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Button
                                onClick={() => window.history.back()}
                                startIcon={<ArrowBackIcon />}
                                variant="outlined"
                                sx={{ 
                                    borderColor: primaryColor,
                                    color: primaryColor,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        bgcolor: alpha(primaryColor, 0.1),
                                        borderColor: primaryColor
                                    }
                                }}
                            >
                                Retour aux actualités
                            </Button>
                            <IconButton 
                                onClick={handleShare}
                                sx={{ 
                                    color: secondaryColor,
                                    bgcolor: alpha(secondaryColor, 0.1),
                                    '&:hover': {
                                        bgcolor: alpha(secondaryColor, 0.2)
                                    }
                                }}
                            >
                                <ShareIcon />
                            </IconButton>
                        </Stack>
                        
                        <Divider sx={{ 
                            borderColor: alpha('#000', 0.1),
                            borderWidth: 1 
                        }} />
                    </Box>
                </Fade>

                {/* --- En-tête Principal --- */}
                <Fade in={true} timeout={800}>
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: isSmallMobile ? 3 : 4, 
                            mb: 4, 
                            textAlign: 'center', 
                            bgcolor: white,
                            borderTop: `5px solid ${primaryColor}`,
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Typography 
                            variant={isSmallMobile ? "h5" : "h4"} 
                            component="h1" 
                            gutterBottom 
                            sx={{ 
                                color: primaryColor, 
                                fontWeight: 'bold',
                                mb: 2
                            }}
                        >
                            CLÔTURE DU 14ÈME MOIS DE LA STATISTIQUE AU BURUNDI
                        </Typography>
                        <Typography 
                            variant={isSmallMobile ? "subtitle1" : "h6"} 
                            component="h2" 
                            sx={{ 
                                color: secondaryColor, 
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Journée Africaine de la Statistique, Édition 2025
                        </Typography>
                    </Paper>
                </Fade>

                {/* --- Image Principale --- */}
                <ImageOptimized 
                    fileId="Cloture_JAS_19-11-2025_Royal_palace-photo_cloture.jpg"
                    alt="Photo de la cérémonie de clôture" 
                    height={isSmallMobile ? 220 : 350}
                    mb={4}
                    priority={true}
                    onLoadComplete={handleImageLoad}
                    objectFit="cover"
                    borderRadius={2}
                    bgColor="#f0f0f0"
                />

                {/* --- Thème de l'Édition --- */}
                <Fade in={true} timeout={1000}>
                    <Paper 
                        sx={{ 
                            p: 3, 
                            mb: 4, 
                            bgcolor: white,
                            borderRadius: 2,
                            textAlign: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: primaryColor, 
                                borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, 
                                display: 'inline-block',
                                pb: 1,
                                mb: 2
                            }}
                        >
                            Thème de l'Édition
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                mt: 2, 
                                mb: 3, 
                                fontWeight: 'medium', 
                                fontSize: isSmallMobile ? '1rem' : '1.1rem', 
                                color: secondaryColor,
                                lineHeight: 1.5
                            }}
                        >
                            « Innovations et statistiques de qualité : leviers incontournables pour l'atteinte de la vision Burundi 2040-2060 »
                        </Typography>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                fontWeight: 'bold', 
                                color: primaryColor
                            }}
                        >
                            Hôtel Royal Palace, Bujumbura - 19 Novembre 2025
                        </Typography>
                    </Paper>
                </Fade>
                
                <Divider sx={{ mb: 4 }} />

                {/* --- Section Discours Détaillés --- */}
                <Typography 
                    variant="h5" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 'bold', 
                        color: primaryColor, 
                        mb: 4, 
                        textAlign: 'center'
                    }}
                >
                    🎙️ Interventions de Haut Niveau
                </Typography>
                
                {discoursContent.map((discours, index) => (
                    <Fade in={true} timeout={500 + (index * 200)} key={index}>
                        <Paper 
                            sx={{ 
                                p: isTablet ? 2 : 3, 
                                mb: 3, 
                                bgcolor: white, 
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                            }}
                        >
                            <Grid container spacing={isSmallMobile ? 2 : 3}>
                                {/* Colonne du Contenu */}
                                <Grid item xs={12} md={7}>
                                    <Box 
                                        display="flex" 
                                        alignItems="center" 
                                        mb={2}
                                        sx={{ 
                                            p: 2, 
                                            borderRadius: 1,
                                            bgcolor: alpha(secondaryColor, 0.05)
                                        }}
                                    >
                                        <discours.icon sx={{ color: secondaryColor, mr: 2, fontSize: isSmallMobile ? 24 : 32 }} />
                                        <Box>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: primaryColor, 
                                                    fontWeight: 'bold',
                                                    display: 'block'
                                                }}
                                            >
                                                {discours.title}
                                            </Typography>
                                            <Typography 
                                                variant={isSmallMobile ? "subtitle1" : "h6"} 
                                                component="h3" 
                                                sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: primaryColor
                                                }}
                                            >
                                                {discours.speaker}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                            mb: 2, 
                                            color: secondaryColor,
                                            fontWeight: 'medium'
                                        }}
                                    >
                                        Messages Clés :
                                    </Typography>
                                    <Box sx={{ pl: 0 }}>
                                        {discours.key_messages.map((message, idx) => (
                                            <Box 
                                                key={idx} 
                                                sx={{ 
                                                    mb: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    bgcolor: alpha(tertiaryColor, 0.5),
                                                    transition: 'background-color 0.3s',
                                                    '&:hover': {
                                                        bgcolor: alpha(secondaryColor, 0.05)
                                                    }
                                                }}
                                            >
                                                <CheckCircleIcon 
                                                    sx={{ 
                                                        color: secondaryColor, 
                                                        fontSize: 16, 
                                                        mr: 1.5, 
                                                        mt: 0.25,
                                                        flexShrink: 0
                                                    }} 
                                                />
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        color: 'text.primary',
                                                        fontSize: isSmallMobile ? '0.875rem' : '0.95rem',
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {message}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                                
                                {/* Colonne de l'Image */}
                                <Grid item xs={12} md={5}>
                                    <ImageOptimized 
                                        fileId={discours.image_file} 
                                        alt={discours.image_alt} 
                                        height={isSmallMobile ? 220 : 280}
                                        mb={0}
                                        priority={discours.priority}
                                        onLoadComplete={handleImageLoad}
                                        objectFit={discours.objectFit || 'contain'}
                                        borderRadius={1}
                                        bgColor="#f8f8f8"
                                    />
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            mt: 1,
                                            textAlign: 'center',
                                            color: alpha(textColor, 0.6),
                                            display: 'block'
                                        }}
                                    >
                                        {discours.speaker.split('(')[0]}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>
                ))}
                
                <Divider sx={{ my: 4 }} />

                {/* --- Section Présentations Thématiques --- */}
                <Box sx={{ mb: 6 }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold', 
                            color: primaryColor, 
                            mb: 3, 
                            textAlign: 'center'
                        }}
                    >
                        📚 Présentations Thématiques
                    </Typography>
                    
                    <Typography 
                        variant="body1" 
                        paragraph 
                        sx={{ 
                            mb: 4, 
                            textAlign: 'center', 
                            color: alpha(textColor, 0.7),
                            fontSize: isSmallMobile ? '0.95rem' : '1rem',
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                    >
                        Les présentations techniques qui ont marqué la cérémonie de clôture
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        {presentationsCloture.map((item, index) => (
                            <Grid item xs={12} sm={6} md={2.4} key={index}>
                                <PresentationCard 
                                    title={item.title} 
                                    description={item.description} 
                                    IconComponent={item.icon}
                                    index={index}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                
                <Divider sx={{ my: 4 }} />

                {/* --- Section Engagements INSBU --- */}
                <Fade in={true} timeout={800}>
                    <Paper 
                        sx={{ 
                            p: isSmallMobile ? 3 : 4,
                            mb: 6,
                            bgcolor: secondaryColor,
                            color: white,
                            borderRadius: 2,
                            boxShadow: `0 8px 24px ${alpha(secondaryColor, 0.3)}`
                        }}
                    >
                        <Typography 
                            variant="h5" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 'bold', 
                                textAlign: 'center',
                                mb: 4
                            }}
                        >
                            Engagements Stratégiques de l'INSBU
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                            {engagementsINSBU.map((engagement, index) => (
                                <Grid item xs={12} sm={6} md={2.4} key={index}>
                                    <Box 
                                        sx={{ 
                                            textAlign: 'center', 
                                            p: 2, 
                                            height: '100%',
                                            transition: 'transform 0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                    >
                                        <engagement.icon sx={{ fontSize: 40, mb: 2, color: alpha(white, 0.9) }} />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontSize: '0.9rem', 
                                                mb: 1,
                                                lineHeight: 1.4
                                            }}
                                        >
                                            {engagement.text}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Fade>
                
                <Divider sx={{ my: 4 }} />

                {/* --- Section Annonces Futures --- */}
                <Box sx={{ mb: 6 }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                            color: primaryColor, 
                            fontWeight: 'bold',
                            textAlign: 'center',
                            mb: 3
                        }}
                    >
                        Perspectives 2026
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {[
                            {
                                icon: EmojiEventsIcon,
                                title: "Concours Jeunes Statisticiens",
                                description: "Pour primer les meilleures jeunes statisticiens à la promotion de la culture statistique.",
                                color: secondaryColor
                            },
                            {
                                icon: CheckCircleIcon,
                                title: "Reconnaissance du CTIS",
                                description: "Remise des certificats aux membres du Comité Technique de l'Information Statistique.",
                                color: primaryColor
                            },
                            {
                                icon: () => <Icon>rss_feed</Icon>,
                                title: "Concours Média",
                                description: "Pour primer les médias qui ont concouru à la promotion de la culture statistique.",
                                color: secondaryColor
                            }
                        ].map((item, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Grow in={true} timeout={300 + (index * 200)}>
                                    <Box 
                                        sx={{ 
                                            p: 3, 
                                            border: `1px solid ${alpha('#e0e0e0', 0.8)}`,
                                            borderRadius: 2,
                                            textAlign: 'center',
                                            height: '100%',
                                            bgcolor: white,
                                            transition: 'all 0.3s',
                                            '&:hover': {
                                                borderColor: item.color,
                                                boxShadow: `0 8px 16px ${alpha(item.color, 0.1)}`
                                            }
                                        }}
                                    >
                                        <Avatar sx={{ 
                                            bgcolor: alpha(item.color, 0.1), 
                                            color: item.color,
                                            width: 60,
                                            height: 60,
                                            mb: 2,
                                            mx: 'auto'
                                        }}>
                                            {item.icon && <item.icon />}
                                        </Avatar>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: primaryColor, mb: 1 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                
                <Divider sx={{ my: 4 }} />

                {/* --- Discours de Clôture --- */}
                <Fade in={true} timeout={800}>
                    <Paper 
                        sx={{ 
                            p: 4, 
                            mb: 4, 
                            bgcolor: alpha(secondaryColor, 0.05), 
                            borderRadius: 2,
                            borderLeft: `4px solid ${secondaryColor}`
                        }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ color: secondaryColor, fontWeight: 'bold', mb: 2 }}>
                            Clôture et Remerciements
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 3, color: primaryColor, fontWeight: 'medium' }}>
                            M. Nicolas NDAYISHIMIYE - Directeur Général de l'INSBU
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: textColor }}>
                            Le Directeur Général a cloturé les cérémonies en remerciant tous les participants, 
                            soulignant l'importance de leur présence pour la cause de la statistique nationale. 
                            Il a réaffirmé l'engagement de l'INSBU à promouvoir une culture statistique durable.
                        </Typography>
                    </Paper>
                </Fade>

                {/* --- Message Final --- */}
                <Fade in={true} timeout={1000}>
                    <Box 
                        sx={{
                            textAlign: 'center',
                            mb: 4
                        }}
                    >
                        <Paper 
                            sx={{ 
                                p: 4,
                                display: 'inline-block',
                                borderRadius: 2,
                                bgcolor: white,
                                border: `2px solid ${alpha(secondaryColor, 0.3)}`,
                                boxShadow: '0 8px 20px rgba(0,0,0,0.05)'
                            }}
                        >
                            <Typography 
                                variant="h6" 
                                component="h3"
                                sx={{ 
                                    fontStyle: 'italic', 
                                    color: secondaryColor,
                                    fontWeight: 'bold',
                                    lineHeight: 1.4
                                }}
                            >
                                Ensemble, innovons et marchons avec des statistiques de qualité en vue de soutenir l'émergence et le développement du Burundi !
                            </Typography>
                        </Paper>
                        <Box sx={{ mt: 3 }}>
                            <Button 
                                onClick={() => window.history.back()}
                                variant="outlined" 
                                startIcon={<ArrowBackIcon />}
                                sx={{ 
                                    borderColor: accentColor, 
                                    color: accentColor,
                                    '&:hover': { borderColor: accentColor, bgcolor: alpha(accentColor, 0.1) },
                                    px: 3, 
                                    py: 1.2, 
                                    fontWeight: 'bold' 
                                }}
                            >
                                Toutes les actualités du MSB
                            </Button>
                        </Box>
                        <Paper 
                            sx={{ 
                                p: 4, 
                                mb: 8, 
                                bgcolor: white, 
                                borderRadius: 3,
                                border: `1px solid ${alpha(accentColor, 0.2)}`, 
                                textAlign: 'center',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                mt: 4
                            }}
                        >
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    color: accentColor, 
                                    fontWeight: 'bold', 
                                    mb: 3 
                                }}
                            >
                                Présenté par Ir. Léandre NIMPAGARITSE, Informaticien – Agent de Publication, Diffusion et Communication à l'INSBU.
                            </Typography>
                        </Paper>
                    </Box>
                </Fade>
            </Container>
            
            <Footer />
        </Box>
    );
}

export default ClotureMoisStatistique2025;